import { useRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Badge,
  Box,
  ButtonBase,
  ClickAwayListener,
  Popper,
  Fade
} from '@mui/material';

// project import
import NotificationsPopup from './NotificationsPopup';
import Transitions from 'components/@extended/Transitions';
import useAuth from "hooks/useAuth";

// store
import useNotificationsStore from './notificationsStore';

// icons
import { MdNotifications } from "react-icons/md";

const NotificationsBadge = () => {
  const theme = useTheme();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const { user, ValidateRole } = useAuth();

  // Badge count = unread notifications (polled by useNotificationsPoller)
  const unreadCount = useNotificationsStore(state => state.unreadCount);

  const validation = ValidateRole(user?.role, [1, 100, 110]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  // If user doesn't have authorized role, don't render the component
  if (validation !== true) return null;

  return (
    <>
      <Box sx={{ flexShrink: 0, ml: 0.75 }}>
        <ButtonBase
          sx={{
            p: 0.25,
            borderRadius: 1,
            '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'secondary.light' : 'secondary.lighter' },
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.secondary.dark}`,
              outlineOffset: 2
            }
          }}
          aria-label="open notifications"
          ref={anchorRef}
          aria-controls={open ? 'notifications-popover' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <Badge badgeContent={unreadCount} color="success">
            <MdNotifications style={{ fontSize: '1.5rem', color: theme.palette.primary.light }} />
          </Badge>
        </ButtonBase>
      </Box>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        transition
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
        sx={{ zIndex: 1500 }}
      >
        {({ TransitionProps }) => (
          <Transitions type="fade" in={open} {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  {open && <NotificationsPopup onClose={() => setOpen(false)} />}
                </Box>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  );
};

export default NotificationsBadge;

// Styled Paper component with fade transition
const Paper = ({ children, sx, ...other }) => {
  return (
    <Fade in>
      <Box
        sx={{
          // Grey surface (ModalDialogV2 template) so the notification capsules
          // (white) stand out.
          bgcolor: 'grey.200',
          boxShadow: '0px 8px 10px rgba(0, 0, 0, 0.14), 0px 3px 14px rgba(0, 0, 0, 0.12)',
          minWidth: 380,
          maxWidth: 620,
          width: '100%',
          borderRadius: 1.5,
          ...sx
        }}
        {...other}
      >
        {children}
      </Box>
    </Fade>
  );
};
